import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X } from 'lucide-react';
import { MaskedInput } from '@/components/ui/masked-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const AddClientModal = ({ onClose, onSuccess, activePeriodId, isAdmin = false }) => {
  const [tipoPessoa, setTipoPessoa] = useState('fisica');
  const [formData, setFormData] = useState({
    nome_completo: '',
    cpf: '',
    razao_social: '',
    cnpj: '',
    email: '',
    telefone: '',
    partner_id: ''
  });
  const [documents, setDocuments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchPartners();
    }
  }, [isAdmin]);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/users/partners`, getAuthHeaders());
      // Ordenar parceiros por nome em ordem alfabética
      const sortedPartners = response.data.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setDocuments([...documents, ...acceptedFiles]);
  }, [documents]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: true
  });

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        tipo_pessoa: tipoPessoa,
        email: formData.email || null,
        telefone: formData.telefone || null,
        period_id: activePeriodId
      };

      if (tipoPessoa === 'fisica') {
        payload.nome_completo = formData.nome_completo;
        payload.cpf = formData.cpf;
      } else {
        payload.razao_social = formData.razao_social;
        payload.cnpj = formData.cnpj;
      }

      // Admin can assign to a partner
      if (isAdmin && formData.partner_id) {
        payload.partner_id = formData.partner_id;
      }

      const clientResponse = await axios.post(`${API}/clients`, payload, getAuthHeaders());
      const clientId = clientResponse.data.id;

      // Upload documentos se houver
      for (const doc of documents) {
        const formData = new FormData();
        formData.append('file', doc);
        formData.append('client_id', clientId);
        
        await axios.post(`${API}/documents/upload`, formData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success('Cliente adicionado com sucesso!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="add-client-modal">
        <DialogHeader>
          <DialogTitle data-testid="add-client-modal-title">Novo Cliente</DialogTitle>
          <DialogDescription>Adicione as informações do cliente</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-client-form">
          {/* Admin can select partner */}
          {isAdmin && partners.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="partner">Atribuir ao Parceiro (opcional)</Label>
              <Select 
                value={formData.partner_id} 
                onValueChange={(value) => setFormData({ ...formData, partner_id: value })}
              >
                <SelectTrigger data-testid="partner-select">
                  <SelectValue placeholder="Selecione um parceiro (ou deixe em branco)" />
                </SelectTrigger>
                <SelectContent>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.name} {partner.company_name ? `(${partner.company_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Se não selecionar, o cliente será atribuído a você
              </p>
            </div>
          )}

          <div className="space-y-3">
            <Label>Tipo de Pessoa</Label>
            <RadioGroup value={tipoPessoa} onValueChange={setTipoPessoa} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fisica" id="fisica" data-testid="radio-fisica" />
                <Label htmlFor="fisica" className="cursor-pointer">Pessoa Física</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="juridica" id="juridica" data-testid="radio-juridica" />
                <Label htmlFor="juridica" className="cursor-pointer">Pessoa Jurídica</Label>
              </div>
            </RadioGroup>
          </div>

          {tipoPessoa === 'fisica' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="nome_completo">Nome Completo *</Label>
                <Input
                  id="nome_completo"
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                  required
                  data-testid="client-name-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpf">CPF *</Label>
                <MaskedInput
                  mask="cpf"
                  id="cpf"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                  required
                  data-testid="client-cpf-input"
                  className="input-focus"
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="razao_social">Razão Social *</Label>
                <Input
                  id="razao_social"
                  value={formData.razao_social}
                  onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                  required
                  data-testid="client-razao-input"
                  className="input-focus"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ *</Label>
                <MaskedInput
                  mask="cnpj"
                  id="cnpj"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  required
                  data-testid="client-cnpj-input"
                  className="input-focus"
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email (opcional)</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              data-testid="client-email-input"
              className="input-focus"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone (opcional)</Label>
            <Input
              id="telefone"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              placeholder="(00) 00000-0000"
              data-testid="client-phone-input"
              className="input-focus"
            />
          </div>

          {/* Upload de Documentos */}
          <div className="space-y-2">
            <Label>Documentos (opcional)</Label>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} />
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isDragActive ? 'Solte os arquivos aqui' : 'Clique ou arraste arquivos (PDF ou JPEG)'}
              </p>
            </div>
            {documents.length > 0 && (
              <div className="space-y-2 mt-3">
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border border-border rounded">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm">{doc.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDocument(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 btn-transition"
              data-testid="cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 btn-transition"
              data-testid="submit-client-button"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddClientModal;
